import React, { useState, useEffect, useRef } from 'react';
import {
  CHARTS,
  PRACTICAL,
  CHALLENGES,
  CATEGORY_CONFIG,
  GROUP_ORDERS
} from '../data/skillTestData';

const STORAGE_KEY = 'MR_EXCEL_SKILL_TEST_V1';
const ASSESSMENT_STEPS = ['shortcuts', 'functions', 'tools', 'charts', 'powerQuery', 'vba', 'practical', 'challenge'];

const summaries = {
  Basic: 'You can complete core Excel tasks, but your next improvement will come from building stronger formula, data-handling and reporting foundations.',
  Intermediate: 'You have a useful working knowledge of Excel and can handle many everyday tasks. Focused practice will help you move from task completion to reliable analysis and automation.',
  Advanced: 'You can solve complex workplace problems across several Excel areas. Your next step is to strengthen weaker technical areas and build more controlled, reusable solutions.',
  Expert: 'You demonstrate broad, validated capability across formulas, analysis, transformation, visualisation and automation. Continue improving model quality, performance and maintainability.'
};

const insightText = {
  shortcuts: ['Efficient navigation and keyboard execution', 'Build daily speed with navigation, selection and formatting shortcuts.'],
  functions: ['Formula logic and calculation', 'Strengthen conditional formulas, lookups and modern dynamic-array functions.'],
  tools: ['Data analysis and PivotTable capability', 'Practise Pivot value calculations, What-If tools and multi-table analysis.'],
  charts: ['Visual communication and dashboard design', 'Improve chart selection, customisation and decision-focused dashboard layout.'],
  powerQuery: ['Repeatable data preparation', 'Build refreshable cleaning, merging, appending and folder-combination processes.'],
  vba: ['Excel automation and code maintenance', 'Develop reliable VBA with dynamic ranges, error handling and debugging.'],
  practical: ['Workplace problem-solving', 'Practise end-to-end projects that combine cleaning, controls, analysis and reporting.']
};

export default function SkillTest({ navigate }) {
  const canvasRef = useRef(null);

  // Core assessment state
  const [currentScreen, setCurrentScreen] = useState('intro');
  const [profile, setProfile] = useState({ frequency: '', goal: '' });
  const [filters, setFilters] = useState({ shortcuts: 'All', functions: 'All', tools: 'All', powerQuery: 'All', vba: 'All' });
  const [answers, setAnswers] = useState({
    shortcuts: {},
    functions: {},
    tools: {},
    charts: {},
    powerQuery: {},
    vba: {},
    practical: {},
    challenge: {}
  });

  const [hasSavedSession, setHasSavedSession] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [copyStatus, setCopyStatus] = useState('Copy result summary');

  // Check for saved state on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.answers && Object.values(parsed.answers).some(g => Object.keys(g).length > 0)) {
          setHasSavedSession(true);
        }
      }
    } catch (_) {}
  }, []);

  // Save progress automatically when state changes
  const saveState = (updatedScreen, updatedAnswers, updatedFilters, updatedProfile) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          current: updatedScreen || currentScreen,
          filters: updatedFilters || filters,
          profile: updatedProfile || profile,
          answers: updatedAnswers || answers
        })
      );
      setHasSavedSession(true);
    } catch (_) {}
  };

  const handleResume = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.current) setCurrentScreen(parsed.current);
        if (parsed.profile) setProfile(parsed.profile);
        if (parsed.filters) setFilters(parsed.filters);
        if (parsed.answers) setAnswers(parsed.answers);
        
        // Scroll to card
        setTimeout(() => {
          document.getElementById('assessment-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    } catch (_) {}
  };

  const handleStart = () => {
    setCurrentScreen('shortcuts');
    setShowWarning(false);
    saveState('shortcuts', answers, filters, profile);
    setTimeout(() => {
      document.getElementById('assessment-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleRestart = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
    setAnswers({
      shortcuts: {},
      functions: {},
      tools: {},
      charts: {},
      powerQuery: {},
      vba: {},
      practical: {},
      challenge: {}
    });
    setFilters({ shortcuts: 'All', functions: 'All', tools: 'All', powerQuery: 'All', vba: 'All' });
    setProfile({ frequency: '', goal: '' });
    setHasSavedSession(false);
    setCurrentScreen('intro');
    setShowReview(false);
  };

  const handleNext = () => {
    const idx = ASSESSMENT_STEPS.indexOf(currentScreen);
    if (currentScreen === 'challenge') {
      // Validate that all challenges are answered
      const answeredCount = Object.keys(answers.challenge).length;
      if (answeredCount < CHALLENGES.length) {
        setShowWarning(true);
        document.getElementById('challenge-warning')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      setShowWarning(false);
      setCurrentScreen('result');
      saveState('result', answers, filters, profile);
    } else {
      const nextScreen = ASSESSMENT_STEPS[idx + 1];
      if (nextScreen) {
        setCurrentScreen(nextScreen);
        saveState(nextScreen, answers, filters, profile);
        setTimeout(() => {
          document.getElementById('assessment-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    }
  };

  const handlePrevious = () => {
    const idx = ASSESSMENT_STEPS.indexOf(currentScreen);
    if (idx > 0) {
      const prevScreen = ASSESSMENT_STEPS[idx - 1];
      setCurrentScreen(prevScreen);
      saveState(prevScreen, answers, filters, profile);
      setTimeout(() => {
        document.getElementById('assessment-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  // Helper labels
  const difficultyLabel = (value) => {
    return { 1: 'essential', 2: 'common', 3: 'advanced', 4: 'expert' }[value] || 'common';
  };

  // Scoring Math
  const getWeightedPercent = (section) => {
    const config = CATEGORY_CONFIG[section];
    const totalWeight = config.items.reduce((sum, item) => sum + item.difficulty, 0);
    let earned = 0;

    config.items.forEach((item) => {
      const val = Number(answers[section][item.id] || 0);
      let factor = 0;
      if (config.mode === 'binary') factor = val ? 1 : 0;
      if (config.mode === 'confidence') factor = { 0: 0, 1: 0.35, 2: 0.7, 3: 1 }[val] || 0;
      if (config.mode === 'practical') factor = { 0: 0, 1: 0.45, 2: 1 }[val] || 0;
      earned += item.difficulty * factor;
    });

    return totalWeight ? (earned / totalWeight) * 100 : 0;
  };

  const calculateScore = () => {
    const categoryPercentages = {};
    let capability = 0;

    Object.entries(CATEGORY_CONFIG).forEach(([key, config]) => {
      const percent = getWeightedPercent(key);
      categoryPercentages[key] = percent;
      capability += (percent / 100) * config.max;
    });

    const correct = CHALLENGES.reduce((sum, q) => sum + (Number(answers.challenge[q.id]) === q.correct ? 1 : 0), 0);
    const verified = (correct / CHALLENGES.length) * 25;
    const rawOverall = capability + verified;
    const categoryCoverage = Object.values(categoryPercentages).filter((val) => val >= 65).length;

    let level = rawOverall <= 34 ? 'Basic' : rawOverall <= 54 ? 'Intermediate' : rawOverall <= 74 ? 'Advanced' : 'Expert';
    let capped = false;

    if (level === 'Expert' && (verified < 17.5 || categoryCoverage < 4)) {
      level = 'Advanced';
      capped = true;
    }
    if (level === 'Advanced' && verified < 12.5) {
      level = 'Intermediate';
      capped = true;
    }

    return {
      capability,
      verified,
      overall: rawOverall,
      displayScore: Math.round(rawOverall),
      level,
      correct,
      categoryPercentages,
      capped
    };
  };

  const results = calculateScore();

  // Radar Canvas Drawing
  const drawRadar = (canvas, percentages) => {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssWidth = Math.max(360, rect.width || 560);
    const cssHeight = Math.round(cssWidth * 0.72);
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    canvas.style.height = `${cssHeight}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const keys = Object.keys(CATEGORY_CONFIG);
    const cx = cssWidth / 2;
    const cy = cssHeight / 2 + 4;
    const radius = Math.min(cssWidth, cssHeight) * 0.31;
    const angleStep = (Math.PI * 2) / keys.length;
    const start = -Math.PI / 2;

    // Background rings
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#d8dfda';
    for (let ring = 1; ring <= 5; ring++) {
      ctx.beginPath();
      keys.forEach((key, index) => {
        const angle = start + index * angleStep;
        const r = (radius * ring) / 5;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.stroke();
    }

    // Axes
    keys.forEach((key, index) => {
      const angle = start + index * angleStep;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
      ctx.stroke();
    });

    // Score Polygon
    ctx.beginPath();
    keys.forEach((key, index) => {
      const angle = start + index * angleStep;
      const r = (radius * Math.max(0, Math.min(100, percentages[key]))) / 100;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = 'rgba(20,108,67,.18)';
    ctx.strokeStyle = '#146c43';
    ctx.lineWidth = 2.4;
    ctx.fill();
    ctx.stroke();

    // Labels
    ctx.font = '700 12px Inter, Arial';
    ctx.fillStyle = '#344239';
    ctx.textBaseline = 'middle';
    keys.forEach((key, index) => {
      const angle = start + index * angleStep;
      const labelRadius = radius + 38;
      let x = cx + Math.cos(angle) * labelRadius;
      let y = cy + Math.sin(angle) * labelRadius;
      if (Math.cos(angle) > 0.3) ctx.textAlign = 'left';
      else if (Math.cos(angle) < -0.3) ctx.textAlign = 'right';
      else ctx.textAlign = 'center';
      const label = CATEGORY_CONFIG[key].label.replace(' & ', ' / ');
      ctx.fillText(label, x, y);
    });
  };

  // Redraw canvas on results load & resize
  useEffect(() => {
    if (currentScreen === 'result' && canvasRef.current) {
      const handleResize = () => {
        if (canvasRef.current) drawRadar(canvasRef.current, results.categoryPercentages);
      };
      drawRadar(canvasRef.current, results.categoryPercentages);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [currentScreen, results]);

  const handleCopySummary = async () => {
    const catsStr = Object.entries(results.categoryPercentages)
      .map(([key, val]) => `${CATEGORY_CONFIG[key].label}: ${Math.round(val)}%`)
      .join('; ');
    const text = `My Excel Skill Assessment: ${results.level} Excel User — ${results.displayScore}/100. Capability: ${results.capability.toFixed(1)}/75. Verified knowledge: ${results.verified.toFixed(1)}/25. ${catsStr}.`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('Result copied!');
      setTimeout(() => setCopyStatus('Copy result summary'), 2000);
    } catch (_) {
      window.prompt('Copy your results:', text);
    }
  };

  // Dynamic content filters
  const getFilteredItems = (section) => {
    const config = CATEGORY_CONFIG[section];
    const activeFilter = filters[section] || 'All';
    return activeFilter === 'All' ? config.items : config.items.filter((item) => item.group === activeFilter);
  };

  // Helper to compile recommendation gaps
  const buildGapSentence = (key, score) => {
    const config = CATEGORY_CONFIG[key];
    const targetItems = config.items
      .filter((item) => {
        const val = Number(answers[key][item.id] || 0);
        return config.mode === 'binary' ? !val : val < 2;
      })
      .sort((a, b) => b.difficulty - a.difficulty)
      .slice(0, 3)
      .map((item) => item.name);
    const examples = targetItems.length ? ` Focus on ${targetItems.join(', ')}.` : '';
    return `${insightText[key][1]}${examples}`;
  };

  const progressIndex = ASSESSMENT_STEPS.indexOf(currentScreen);
  const progressPercent = progressIndex !== -1 ? Math.round(((progressIndex + 1) / ASSESSMENT_STEPS.length) * 100) : 0;

  return (
    <main>
      <section className="page-hero">
        <div className="container">
          <span className="eyebrow">Practical Excel assessment</span>
          <h1>Identify your Excel capability level.</h1>
          <p className="lead max-820">
            A structured self-assessment covering keyboard speed, formulas, reporting, data models and automation, validated by a visual knowledge check.
          </p>
        </div>
      </section>

      {/* Progress Bar Container */}
      {currentScreen !== 'intro' && currentScreen !== 'result' && (
        <div className="assessment-progress-wrap" id="assessment-progress-wrap">
          <div className="container">
            <div className="progress-bar-container">
              <div className="progress-labels">
                <span id="progress-step-label">Step {progressIndex + 1} of {ASSESSMENT_STEPS.length}</span>
                <strong id="progress-section-label">
                  {currentScreen === 'powerQuery' ? 'Power Query' : CATEGORY_CONFIG[currentScreen]?.label || 'Knowledge Check'}
                </strong>
                <span id="progress-percent">{progressPercent}%</span>
              </div>
              <div className="progress-track" aria-hidden="true">
                <span id="progress-fill" style={{ width: `${progressPercent}%` }}></span>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="assessment-shell">
        <div className="container">
          <div className="assessment-card" id="assessment-card">
            
            {/* Screen 1: Intro */}
            {currentScreen === 'intro' && (
              <section className="assessment-screen intro-screen active">
                <div className="intro-grid">
                  <div>
                    <span className="section-kicker">Before you begin</span>
                    <h2>This is a practical capability check, not an exam.</h2>
                    <p>Select only the skills you can use without searching for every step. You do not lose marks for leaving a skill unselected.</p>
                    <ul className="assessment-rules">
                      <li>
                        <span>1</span>
                        <div>
                          <strong>Be honest</strong>
                          <p>Choose a skill only when you understand how and when to use it.</p>
                        </div>
                      </li>
                      <li>
                        <span>2</span>
                        <div>
                          <strong>Use confidence levels where shown</strong>
                          <p>Recognising a chart is different from creating and customising it.</p>
                        </div>
                      </li>
                      <li>
                        <span>3</span>
                        <div>
                          <strong>Complete the visual check</strong>
                          <p>The final questions validate practical understanding and reduce over-scoring.</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div className="profile-panel">
                    <h3>Optional profile</h3>
                    <p>This helps make the recommendations more relevant. It does not affect your score.</p>
                    <label>
                      How often do you use Excel?
                      <select
                        id="profile-frequency"
                        value={profile.frequency}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProfile((prev) => ({ ...prev, frequency: val }));
                          saveState(null, null, null, { ...profile, frequency: val });
                        }}
                      >
                        <option value="">Select one</option>
                        <option>Occasionally</option>
                        <option>A few times per week</option>
                        <option>Daily</option>
                        <option>Most of my working day</option>
                      </select>
                    </label>
                    <label>
                      Your main reason for using Excel
                      <select
                        id="profile-goal"
                        value={profile.goal}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProfile((prev) => ({ ...prev, goal: val }));
                          saveState(null, null, null, { ...profile, goal: val });
                        }}
                      >
                        <option value="">Select one</option>
                        <option>Workplace reporting</option>
                        <option>Finance and analysis</option>
                        <option>Operations and planning</option>
                        <option>HR and administration</option>
                        <option>Freelancing and automation</option>
                        <option>Learning for a new job</option>
                      </select>
                    </label>
                    <button className="assessment-primary" id="start-assessment" type="button" onClick={handleStart}>
                      Start the assessment <span>→</span>
                    </button>
                    {hasSavedSession && (
                      <button className="resume-button" id="resume-assessment" type="button" onClick={handleResume}>
                        Resume saved assessment
                      </button>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Binary Checklists (Shortcuts, Functions, Tools, PowerQuery, VBA) */}
            {['shortcuts', 'functions', 'tools', 'powerQuery', 'vba'].includes(currentScreen) && (
              <section className="assessment-screen active">
                <div className="section-heading-row">
                  <div>
                    <span className="section-kicker">
                      {currentScreen === 'shortcuts' && '01 · Productivity'}
                      {currentScreen === 'functions' && '02 · Formulas'}
                      {currentScreen === 'tools' && '03 · Tools & PivotTables'}
                      {currentScreen === 'powerQuery' && '05 · Power Query'}
                      {currentScreen === 'vba' && '06 · VBA & Automation'}
                    </span>
                    <h2>
                      {currentScreen === 'shortcuts' && 'Which Excel shortcuts can you use confidently?'}
                      {currentScreen === 'functions' && 'Which Excel functions can you apply correctly?'}
                      {currentScreen === 'tools' && 'Which Excel tools can you use independently?'}
                      {currentScreen === 'powerQuery' && 'Which data transformation tasks can you complete?'}
                      {currentScreen === 'vba' && 'Which automation skills can you use safely?'}
                    </h2>
                    <p>
                      {currentScreen === 'shortcuts' && 'Select every shortcut you can use from memory in the correct situation.'}
                      {currentScreen === 'functions' && 'Select functions you understand well enough to use in a real workbook, not functions you have only heard about.'}
                      {currentScreen === 'tools' && 'This section covers everyday data data tools, analysis features and advanced PivotTable capabilities.'}
                      {currentScreen === 'powerQuery' && 'Select the Power Query actions you can perform and refresh reliably in a working file.'}
                      {currentScreen === 'vba' && 'Select only the VBA or macro skills you can apply, test and maintain in a real workbook.'}
                    </p>
                  </div>
                  <div className="selection-counter">
                    <strong>{Object.values(answers[currentScreen]).filter(Boolean).length}</strong>
                    <span> selected</span>
                  </div>
                </div>
                
                {/* Filters */}
                <div className="filter-toolbar">
                  {['All', ...(GROUP_ORDERS[currentScreen] || [])].map((g) => (
                    <button
                      key={g}
                      className={`filter-chip ${filters[currentScreen] === g ? 'active' : ''}`}
                      type="button"
                      onClick={() => {
                        const updated = { ...filters, [currentScreen]: g };
                        setFilters(updated);
                        saveState(null, null, updated, null);
                      }}
                    >
                      {g}
                    </button>
                  ))}
                </div>

                {/* Items Checklist Grid */}
                <div className="selection-grid">
                  {getFilteredItems(currentScreen).map((item) => {
                    const isSelected = Boolean(answers[currentScreen][item.id]);
                    return (
                      <button
                        key={item.id}
                        className={`skill-select ${isSelected ? 'selected' : ''}`}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => {
                          const updatedAnswers = {
                            ...answers,
                            [currentScreen]: {
                              ...answers[currentScreen],
                              [item.id]: !isSelected
                            }
                          };
                          setAnswers(updatedAnswers);
                          saveState(null, updatedAnswers, null, null);
                        }}
                      >
                        <span className="select-check">✓</span>
                        <span className="skill-name">{item.name}</span>
                        {item.key && <span className="skill-key">{item.key}</span>}
                        <span className="skill-meta">
                          <span>{item.group}</span>
                          <span className="difficulty-dot" data-difficulty={item.difficulty}>
                            {difficultyLabel(item.difficulty)}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Screen 4: Charts */}
            {currentScreen === 'charts' && (
              <section className="assessment-screen active">
                <div className="section-heading-row">
                  <div>
                    <span className="section-kicker">04 · Charts & Dashboards</span>
                    <h2>How confidently can you work with each chart?</h2>
                    <p>Choose your highest genuine level: recognise it, create it, or customise it for professional reporting.</p>
                  </div>
                  <div className="selection-counter">
                    <strong>{Object.values(answers.charts).filter(v => Number(v) > 0).length}</strong>
                    <span> charts rated</span>
                  </div>
                </div>
                <div className="confidence-legend">
                  <span><i>1</i> Recognise</span>
                  <span><i>2</i> Create</span>
                  <span><i>3</i> Customise</span>
                </div>
                <div className="confidence-grid">
                  {CHARTS.map((item) => {
                    const val = Number(answers.charts[item.id] || 0);
                    return (
                      <article key={item.id} className="confidence-card">
                        <h3>{item.name}</h3>
                        <p>{item.key}</p>
                        <div className="confidence-options" role="group" aria-label={`Confidence for ${item.name}`}>
                          {['Recognise', 'Create', 'Customise'].map((label, idx) => {
                            const level = idx + 1;
                            return (
                              <button
                                key={level}
                                className={`confidence-option ${val === level ? 'active' : ''}`}
                                type="button"
                                aria-pressed={val === level}
                                onClick={() => {
                                  const updatedAnswers = {
                                    ...answers,
                                    charts: {
                                      ...answers.charts,
                                      [item.id]: val === level ? 0 : level
                                    }
                                  };
                                  setAnswers(updatedAnswers);
                                  saveState(null, updatedAnswers, null, null);
                                }}
                              >
                                {level}. {label}
                              </button>
                            );
                          })}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Screen 7: Practical Workplace know-how */}
            {currentScreen === 'practical' && (
              <section className="assessment-screen active">
                <div className="section-heading-row">
                  <div>
                    <span className="section-kicker">07 · Practical Workplace Know-how</span>
                    <h2>What can you build or solve in a real job?</h2>
                    <p>For each task, choose whether you can complete it with guidance or independently.</p>
                  </div>
                  <div className="selection-counter">
                    <strong>{Object.values(answers.practical).filter(v => Number(v) > 0).length}</strong>
                    <span> tasks rated</span>
                  </div>
                </div>
                <div className="practical-legend">
                  <span>With guidance = partial score</span>
                  <span>Independently = full score</span>
                </div>
                <div className="practical-grid">
                  {PRACTICAL.map((item) => {
                    const val = Number(answers.practical[item.id] || 0);
                    return (
                      <article key={item.id} className="practical-card">
                        <div>
                          <h3>{item.name}</h3>
                          <p>{item.key}</p>
                        </div>
                        <div className="practical-options" role="group" aria-label={`Capability for ${item.name}`}>
                          <button
                            className={`practical-option ${val === 1 ? 'active' : ''}`}
                            type="button"
                            aria-pressed={val === 1}
                            onClick={() => {
                              const updatedAnswers = {
                                ...answers,
                                practical: {
                                  ...answers.practical,
                                  [item.id]: val === 1 ? 0 : 1
                                }
                              };
                              setAnswers(updatedAnswers);
                              saveState(null, updatedAnswers, null, null);
                            }}
                          >
                            With guidance
                          </button>
                          <button
                            className={`practical-option ${val === 2 ? 'active' : ''}`}
                            type="button"
                            aria-pressed={val === 2}
                            onClick={() => {
                              const updatedAnswers = {
                                ...answers,
                                practical: {
                                  ...answers.practical,
                                  [item.id]: val === 2 ? 0 : 2
                                }
                              };
                              setAnswers(updatedAnswers);
                              saveState(null, updatedAnswers, null, null);
                            }}
                          >
                            Independently
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Screen 8: Visual Challenges */}
            {currentScreen === 'challenge' && (
              <section className="assessment-screen active">
                <div className="section-heading-row">
                  <div>
                    <span className="section-kicker">08 · Visual Knowledge Check</span>
                    <h2>Apply your knowledge to practical Excel situations.</h2>
                    <p>Choose one answer for each scenario. Select “I’m not sure” instead of guessing when you do not know.</p>
                  </div>
                  <div className="selection-counter">
                    <strong>{Object.keys(answers.challenge).length}</strong>
                    <span> of {CHALLENGES.length} answered</span>
                  </div>
                </div>
                <div className="challenge-list">
                  {CHALLENGES.map((q, qIdx) => {
                    const selectedIdx = answers.challenge[q.id];
                    return (
                      <article key={q.id} className="challenge-card">
                        <div className="challenge-body">
                          <div className="challenge-visual">
                            {/* Notice: since images are copied to /assets/images/ in public, we serve them directly */}
                            <img src={`/${q.image}`} alt={`Question ${qIdx + 1}`} loading="lazy" />
                          </div>
                          <div className="challenge-content">
                            <span className="challenge-number">
                              Question {qIdx + 1} · {CATEGORY_CONFIG[q.category]?.label || 'Practical'}
                            </span>
                            <h3>{q.title}</h3>
                            <p>{q.prompt}</p>
                            <div className="challenge-options">
                              {q.options.map((option, optIdx) => {
                                const isActive = selectedIdx === optIdx;
                                return (
                                  <button
                                    key={optIdx}
                                    className={`challenge-option ${isActive ? 'active' : ''}`}
                                    type="button"
                                    aria-pressed={isActive}
                                    onClick={() => {
                                      const updatedAnswers = {
                                        ...answers,
                                        challenge: {
                                          ...answers.challenge,
                                          [q.id]: optIdx
                                        }
                                      };
                                      setAnswers(updatedAnswers);
                                      saveState(null, updatedAnswers, null, null);
                                    }}
                                  >
                                    <span>{String.fromCharCode(65 + optIdx)}</span>
                                    <span>{option}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
                {showWarning && (
                  <div className="challenge-warning" id="challenge-warning">
                    Please answer all eight questions before viewing your result.
                  </div>
                )}
              </section>
            )}

            {/* Screen 9: Results screen */}
            {currentScreen === 'result' && (
              <section className="assessment-screen result-screen active">
                <div className="result-hero">
                  <div
                    className="score-ring"
                    id="score-ring"
                    style={{ '--score-angle': `${Math.min(360, results.displayScore * 3.6)}deg` }}
                  >
                    <div>
                      <strong>{results.displayScore}</strong>
                      <span>/ 100</span>
                    </div>
                  </div>
                  <div className="result-title-block">
                    <span className="result-overline">Your assessed Excel level</span>
                    <h2>{results.level} Excel User</h2>
                    <p>{summaries[results.level]}</p>
                    <div className="result-badges">
                      {profile.frequency && <span>{profile.frequency}</span>}
                      {profile.goal && <span>{profile.goal}</span>}
                      <span>{results.correct}/8 visual answers correct</span>
                      {results.capped && <span>Level validation applied</span>}
                    </div>
                  </div>
                </div>

                <div className="result-dashboard-grid">
                  <section className="result-panel">
                    <div className="panel-title-row">
                      <div>
                        <span className="section-kicker">Skill profile</span>
                        <h3>Category performance</h3>
                      </div>
                      <span className="panel-note">Self-assessed capability</span>
                    </div>
                    {/* Render Canvas Chart */}
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <canvas ref={canvasRef} style={{ width: '100%', maxWidth: '560px' }}></canvas>
                    </div>
                    <div className="category-bars">
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                        const pct = Math.round(results.categoryPercentages[key]);
                        return (
                          <div key={key} className="category-bar-row">
                            <span>{config.label}</span>
                            <div className="category-bar-track">
                              <i style={{ width: `${pct}%` }}></i>
                            </div>
                            <strong>{pct}%</strong>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                  <section className="result-panel verification-panel">
                    <span className="section-kicker">Score validation</span>
                    <h3>Capability vs. verified knowledge</h3>
                    <div className="verification-score-row">
                      <div>
                        <strong>{results.capability.toFixed(1)}</strong>
                        <span>/ 75 capability</span>
                      </div>
                      <div>
                        <strong>{results.verified.toFixed(1)}</strong>
                        <span>/ 25 verified</span>
                      </div>
                    </div>
                    
                    {/* Calibration text */}
                    <div className="calibration-message">
                      {(() => {
                        const capabilityPct = (results.capability / 75) * 100;
                        const verifiedPct = (results.verified / 25) * 100;
                        const gap = capabilityPct - verifiedPct;
                        let msg = '';
                        if (Math.abs(gap) <= 15) {
                          msg = 'Your selected capabilities and visual challenge performance are broadly aligned. This makes the assessed level more reliable.';
                        } else if (gap > 15) {
                          msg = 'Your selected capability is higher than your visual challenge performance. Prioritise applying familiar tools without step-by-step assistance.';
                        } else {
                          msg = 'Your visual challenge performance is stronger than your self-rating. You may be underestimating skills you already understand and should practise using them more confidently.';
                        }
                        if (results.capped) {
                          msg += ' The displayed level was capped because Advanced and Expert levels require a minimum verified knowledge score.';
                        }
                        return msg;
                      })()}
                    </div>

                    <div className="level-scale">
                      <div className={results.level === 'Basic' ? 'current' : ''}>
                        <span>Basic</span>
                        <small>0–34</small>
                      </div>
                      <div className={results.level === 'Intermediate' ? 'current' : ''}>
                        <span>Intermediate</span>
                        <small>35–54</small>
                      </div>
                      <div className={results.level === 'Advanced' ? 'current' : ''}>
                        <span>Advanced</span>
                        <small>55–74</small>
                      </div>
                      <div className={results.level === 'Expert' ? 'current' : ''}>
                        <span>Expert</span>
                        <small>75–100</small>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Gaps / Strengths */}
                <div className="insight-grid">
                  <section className="result-panel">
                    <span className="section-kicker">What you do well</span>
                    <h3>Your strongest areas</h3>
                    <div className="insight-list">
                      {Object.entries(results.categoryPercentages)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 3)
                        .map(([key, score], idx) => (
                          <div key={key} className="insight-item">
                            <span>{idx + 1}</span>
                            <div>
                              <strong>{CATEGORY_CONFIG[key].label} · {Math.round(score)}%</strong>
                              <p>{insightText[key][0]}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </section>
                  <section className="result-panel">
                    <span className="section-kicker">Highest value gaps</span>
                    <h3>Skills to improve next</h3>
                    <div className="insight-list">
                      {Object.entries(results.categoryPercentages)
                        .sort((a, b) => a[1] - b[1]) // ascending
                        .slice(0, 3)
                        .map(([key, score], idx) => (
                          <div key={key} className="insight-item">
                            <span>{idx + 1}</span>
                            <div>
                              <strong>{CATEGORY_CONFIG[key].label} · {Math.round(score)}%</strong>
                              <p>{buildGapSentence(key, score)}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </section>
                </div>

                {/* Recommendations */}
                <section className="result-panel learning-plan-panel">
                  <div className="panel-title-row">
                    <div>
                      <span className="section-kicker">Personalised recommendation</span>
                      <h3>Your next Excel learning plan</h3>
                    </div>
                    <a className="text-link" href="#/contact" onClick={(e) => { e.preventDefault(); navigate('contact'); }}>
                      Explore training programmes →
                    </a>
                  </div>
                  <div className="learning-path">
                    {Object.entries(results.categoryPercentages)
                      .sort((a, b) => a[1] - b[1]) // gaps first
                      .slice(0, 3)
                      .map(([key, score], idx) => {
                        const config = CATEGORY_CONFIG[key];
                        const stage = score < 35 ? 'Build the foundation' : score < 65 ? 'Develop practical fluency' : 'Close advanced gaps';
                        return (
                          <article key={key} className="learning-step">
                            <span className="step-number">{idx + 1}</span>
                            <h4>{stage}: {config.label}</h4>
                            <p>{config.course}. Practise with a complete workplace example rather than isolated commands.</p>
                            <a href={config.link} onClick={(e) => {
                              if (config.link.startsWith('#/')) {
                                e.preventDefault();
                                navigate(config.link.substring(2));
                              }
                            }}>
                              Open the relevant learning resource →
                            </a>
                          </article>
                        );
                      })}
                  </div>
                </section>

                {/* Challenges review list toggle */}
                <section className="result-panel challenge-review-panel">
                  <div className="panel-title-row">
                    <div>
                      <span className="section-kicker">Knowledge review</span>
                      <h3>Visual challenge answers</h3>
                    </div>
                    <button
                      className="text-button"
                      type="button"
                      aria-expanded={showReview}
                      onClick={() => setShowReview(!showReview)}
                    >
                      {showReview ? 'Hide answer review' : 'Show answer review'}
                    </button>
                  </div>
                  {showReview && (
                    <div className="challenge-review-list" style={{ marginTop: '20px' }}>
                      {CHALLENGES.map((q, idx) => {
                        const selectedIdx = Number(answers.challenge[q.id]);
                        const isCorrect = selectedIdx === q.correct;
                        const selectedText = q.options[selectedIdx] || 'No answer';
                        return (
                          <article key={q.id} className={`review-item ${isCorrect ? 'correct' : 'incorrect'}`} style={{ padding: '12px 16px', borderLeft: isCorrect ? '3px solid #146c43' : '3px solid #dc3545', background: '#fbfbfb', margin: '10px 0', borderRadius: '4px' }}>
                            <strong>{idx + 1}. {q.title} — {isCorrect ? 'Correct' : 'Review needed'}</strong>
                            <p><b>Your answer:</b> {selectedText}</p>
                            <p><b>Correct answer:</b> {q.options[q.correct]}</p>
                            <p style={{ fontStyle: 'italic', fontSize: '0.9rem', color: '#555', marginTop: '6px' }}>{q.explanation}</p>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* Final actions */}
                <div className="result-actions">
                  <button className="assessment-primary" type="button" onClick={() => window.print()}>
                    Print / Save result as PDF
                  </button>
                  <button className="assessment-secondary" type="button" onClick={handleCopySummary}>
                    {copyStatus}
                  </button>
                  <button className="assessment-secondary" type="button" onClick={handleRestart}>
                    Retake assessment
                  </button>
                </div>
              </section>
            )}

            {/* Assessment bottom nav links */}
            {currentScreen !== 'intro' && currentScreen !== 'result' && (
              <div className="assessment-navigation" id="assessment-navigation">
                <button
                  className="assessment-secondary"
                  id="previous-step"
                  type="button"
                  onClick={handlePrevious}
                  style={{ visibility: currentScreen === ASSESSMENT_STEPS[0] ? 'hidden' : 'visible' }}
                >
                  ← Previous
                </button>
                <div className="save-status" id="save-status">
                  Progress saved automatically
                </div>
                <button className="assessment-primary" id="next-step" type="button" onClick={handleNext}>
                  {currentScreen === 'challenge' ? 'View my result' : 'Continue'} <span>→</span>
                </button>
              </div>
            )}

          </div>
        </div>
      </section>

      <section className="skill-test-note">
        <div className="container note-grid">
          <div>
            <span className="eyebrow">Important</span>
            <h2>A score is a guide, not a professional certification.</h2>
          </div>
          <p>
            The result reflects the skills selected and answers provided in this assessment. Actual workplace proficiency also depends on speed, accuracy, problem-solving, file quality and the ability to apply Excel independently.
          </p>
        </div>
      </section>
    </main>
  );
}
